import React from 'react';
import { motion } from 'framer-motion';
import { useGeneralHooks } from '../../../../Hooks/hooks';
import { useTheme } from '../../../../Context/ThemeContext';
import Button from '../../../Button/button';
import { ButtonTypes } from '../../../../Interfaces/componentTypes';
import PhoneFrame from '../../../PhoneFrame/phoneFrame';
import styles from './designCustomization.module.scss';

const FeatureCard: React.FC<{
  title: string;
  description: string;
  features: string[];
  icon: string;
  color: string;
}> = ({ title, description, features, icon, color }) => {
  return (
    <motion.div
      className={styles.featureCard}
      style={{ '--card-color': color } as React.CSSProperties}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
    >
      <div className={styles.cardIcon} style={{ backgroundColor: color }}>
        <span className={styles.iconText}>{icon}</span>
      </div>
      <h3 className={styles.cardTitle}>{title}</h3>
      <p className={styles.cardDescription}>{description}</p>
      <ul className={styles.featuresList}>
        {features.map((feature, index) => (
          <li key={index} className={styles.featureItem}>
            <div className={styles.checkmark} style={{ backgroundColor: color }}>✓</div>
            {feature}
          </li>
        ))}
      </ul>
    </motion.div>
  );
};

const DesignCustomization: React.FC = () => {
  const { t, navigate, largeScreen } = useGeneralHooks();
  const { theme } = useTheme();

  const features = [
    {
      title: t('Main.Design.Branding.Title', 'Brand Identity'),
      description: t('Main.Design.Branding.Description', 'Customize colors, fonts, and logos to match your brand perfectly.'),
      features: [
        t('Main.Design.Branding.Feature1', 'Custom color schemes'),
        t('Main.Design.Branding.Feature2', 'Logo integration'),
        t('Main.Design.Branding.Feature3', 'Brand font selection'),
        t('Main.Design.Branding.Feature4', 'Consistent visual identity')
      ],
      icon: '🎨',
      color: '#ee163a'
    },
    // {
    //   title: t('Main.Design.Templates.Title', 'Menu Templates'),
    //   description: t('Main.Design.Templates.Description', 'Choose from professional templates or create your own unique design.'),
    //   features: [
    //     t('Main.Design.Templates.Feature1', 'Professional templates'),
    //     t('Main.Design.Templates.Feature2', 'Custom layouts'),
    //     t('Main.Design.Templates.Feature3', 'Drag & drop editor'),
    //     t('Main.Design.Templates.Feature4', 'Mobile-optimized designs')
    //   ],
    //   icon: '📋',
    //   color: '#006af5'
    // },
    {
      title: t('Main.Design.Responsive.Title', 'Mobile First'),
      description: t('Main.Design.Responsive.Description', 'Your menu looks perfect on all devices with responsive design.'),
      features: [
        t('Main.Design.Responsive.Feature1', 'Mobile-optimized layouts'),
        t('Main.Design.Responsive.Feature2', 'Touch-friendly interface'),
        t('Main.Design.Responsive.Feature3', 'Fast loading times'),
        t('Main.Design.Responsive.Feature4', 'Cross-platform compatibility')
      ],
      icon: '📱',
      color: '#177558'
    },
    {
      title: t('Main.Design.Professional.Title', 'Professional Support'),
      description: t('Main.Design.Professional.Description', 'Get expert help to create the perfect design for your business.'),
      features: [
        t('Main.Design.Professional.Feature1', 'Design consultation'),
        t('Main.Design.Professional.Feature2', 'Custom implementation'),
        t('Main.Design.Professional.Feature3', 'Brand guidelines'),
        t('Main.Design.Professional.Feature4', 'Ongoing support')
      ],
      icon: '💼',
      color: '#eca624'
    }
  ];

  return (
    <div className={styles.container}>
      <motion.div
        className={styles.header}
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
      >
        <div className={styles.badge}>
          <div className={styles.badgeIcon}>🎨</div>
          <span>{t('Main.Design.Badge', 'New Feature')}</span>
        </div>
        <h2 className={styles.title}>
          {t('Main.Design.Title', 'Design Your Perfect Menu')}
        </h2>
        <p className={styles.subtitle}>
          {t('Main.Design.Subtitle', 'Create a stunning, branded experience that reflects your restaurant\'s unique personality and attracts more customers.')}
        </p>
      </motion.div>

      <div className={styles.featuresGrid}>
        {features.map((feature, index) => (
          <FeatureCard
            key={index}
            title={feature.title}
            description={feature.description}
            features={feature.features}
            icon={feature.icon}
            color={feature.color}
          />
        ))}
      </div>

      <motion.div
        className={styles.showcase}
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.6, ease: 'easeOut', delay: 0.2 }}
      >
        <div className={styles.showcaseContent}>
          <div className={styles.showcaseText}>
            <h3 className={styles.showcaseTitle}>
              {t('Main.Design.Showcase.Title', 'See It In Action')}
            </h3>
            <p className={styles.showcaseDescription}>
              {t('Main.Design.Showcase.Description', 'Watch how easy it is to customize your menu with our intuitive design tools. Create a professional look that matches your brand in minutes.')}
            </p>
            <div className={styles.showcaseFeatures}>
              <div className={styles.showcaseFeature}>
                <div className={styles.checkmark}>✓</div>
                <span>{t('Main.Design.Showcase.Feature1', 'No coding required')}</span>
              </div>
              <div className={styles.showcaseFeature}>
                <div className={styles.checkmark}>✓</div>
                <span>{t('Main.Design.Showcase.Feature2', 'Real-time preview')}</span>
              </div>
              <div className={styles.showcaseFeature}>
                <div className={styles.checkmark}>✓</div>
                <span>{t('Main.Design.Showcase.Feature3', 'Professional results')}</span>
              </div>
            </div>
          </div>
          <div className={styles.showcaseVisual}>
            <PhoneFrame />
          </div>
        </div>
      </motion.div>

      <motion.div
        className={styles.cta}
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.6, ease: 'easeOut', delay: 0.4 }}
      >
        <div className={styles.ctaContent}>
          <h3 className={styles.ctaTitle}>
            {t('Main.Design.CTA.Title', 'Ready to Create Your Perfect Menu?')}
          </h3>
          <p className={styles.ctaDescription}>
            {t('Main.Design.CTA.Description', 'Join thousands of restaurants who have already transformed their digital presence with our design tools.')}
          </p>
          <div className={styles.ctaButtons}>
            <Button
              buttonType={ButtonTypes.Primery}
              title={t('Main.Design.CTA.Button1', 'Start Designing')}
              buttonStyle={styles.primaryButton}
              onClick={() => navigate('/signUp')}
            />
            {/* <Button
              buttonType={ButtonTypes.Secondary}
              title={t('Main.Design.CTA.Button2', 'View Examples')}
              buttonStyle={styles.secondaryButton}
              onClick={() => navigate('/examples')}
            /> */}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default DesignCustomization;
